/**
 * §A4 — one roster panel of the Reporting page.
 *
 * Both panels are the same component, as §A4 requires (*"identical in structure to the
 * roster on module 05, and built from the same component"*): the technical panel passes the
 * `ATD` / `TO` / `Judge` tabs and the umpiring panel the `UM` / `Umpire` ones, and everything
 * else — the columns, the row contract, the count line, the empty state — is shared.
 *
 * **Columns, exactly §A4's eight:** `Official Role` · *(blank, sr-only `Profile Picture`)* ·
 * `Full Name` · `Country` · `Cap` · `Report Status` · `Email Address` · `Mobile Number`.
 *
 * **Where the name link goes.** §A4's panel note says *"click any row to open that profile in
 * a new tab"*, and module 05's individual profile is now built at `/officials/:tmsPersonId`,
 * so the name goes there — the marking screen it stood in for while the profile was a stub is
 * one click on from it. Two deliberate readings of §A3, both stated in the panel note the
 * page passes in:
 *
 *  · **New tab, kept.** §A3 is unambiguous about it — `target="_blank" rel="noopener"`, with
 *    a hover badge — and the note says so, so the control and its description agree.
 *  · **The name, not the row.** §A3 makes the whole `<tr>` clickable with JS. A link is the
 *    honest control (it can be focused, opened in a background tab and read by a screen
 *    reader as what it is), so the note reads *Click Any Name*, one word off the mockup's
 *    copy, rather than promising a row click that is not there.
 *
 * **The optional search narrowing (CC-Q11, ruled 2026-08-11).** The ruling is one masthead
 * search over people, matches and cases, *"if time is short, ship search on the roster page
 * only"* — so module 05's roster owns a search field and hands its text down here as
 * {@link RosterPanelProps.query}. It is optional and defaults to no narrowing at all, which
 * is what the Reporting page passes: that page's §A4 draws no search field, and it does not
 * grow one because this component gained the ability to honour one.
 *
 * **Why the narrowing lives here rather than in the page.** The count line reads `{n} Of
 * {total} Officials Shown`, and `total` is the whole panel — filtering the array before it
 * arrived would silently shrink the denominator too, so a search for one name would read
 * `1 Of 1` and tell the TD nothing about what he had hidden.
 *
 * **RUN 50 — `data-label` on every cell, and why the markup gained an attribute.** The
 * Technical Delegate opened this panel on a phone and read eight columns painted on top of
 * one another: `table-layout: fixed` divides the width it is given rather than widening for
 * its content, and 339px does not divide eight ways. Below 900px the stylesheet therefore
 * redraws each row as one card — name and role above country and cap, then the status pill,
 * the address and the number on lines of their own — and hides the `<thead>` that named the
 * columns. Three cells lose their meaning with that header (`Cap`, `Email Address`,
 * `Mobile Number`) and draw it back from `data-label`; the other five caption themselves. The
 * attribute is written on **all eight** cells rather than only the three the CSS draws, so
 * that the contract is one rule and a future card layout can label any of them without a
 * change here. Nothing about the row's behaviour changed: the same eight cells, in §A3's
 * order, and the name is still the link that opens the profile in a new tab.
 *
 * **`FIH Grading` — a ninth column, on The Tournament Roster only.** The Technical Delegate
 * wanted each official's current FIH grading readable down the roster, so the page that draws
 * it asks for it through {@link RosterPanelProps.showGrading}, exactly as it asks for the `xl`
 * pictures; the Reporting page's §A4 pair keep their eight. It sits after `Cap` because the
 * two are read together — how much this person has done, and at what standing — and it
 * prints the grading for the role held AT THIS TOURNAMENT or an honest `—`, never a grade
 * from another career (see `ReportProgressRow.gradingLevel`).
 */

import { Link } from "react-router-dom";

import type { OfficialRole } from "@hockeytd/shared";

import type { ReportProgressRow } from "../../api/types.js";
import { CountryFlag } from "../../app/CountryFlag.js";
import { EmptyState } from "../../app/states.js";
import { OfficialAvatar } from "../officials/OfficialAvatar.js";
import { CAP_UNIT_SHORT, capTooltip } from "./capUnit.js";
import { OwnedByUmMarker, ReportStatusPill } from "./ReportStatusPill.js";

/** §A3's panel note, in the form this build's control actually earns. */
export const ROSTER_PANEL_NOTE = "Click Any Name To Open That Profile In A New Tab";

/** The role chip's modifier, per `04-officials-profiles.md` §A3. */
function roleChipModifier(role: OfficialRole): string {
  switch (role) {
    case "ATD":
      return "atd";
    case "TO":
      return "to";
    case "Judge":
      return "jg";
    case "UM":
      return "um";
    case "Umpire":
      return "ump";
    default:
      return "other";
  }
}

/**
 * Whether one row answers the roster search — a case-insensitive match over the four columns
 * that identify a person: the name the roster prints, the IOC code, the role and the email
 * address.
 *
 * **Every word must match, and the order does not.** The roster prints `LINDQVIST Bo`, and a
 * TD looking for him will type either half in either order; a plain substring test would
 * answer `lindqvist bo` and refuse `bo lindqvist`, which is a distinction nobody typing into
 * a search field is making. Splitting on whitespace and requiring each token also makes
 * `ned umpire` a usable narrowing across two columns at once.
 *
 * **The mobile number is deliberately not searched.** A telephone number is not how anybody
 * looks a person up, and matching on it would let a stray digit surface a row whose name,
 * country and role all say otherwise.
 */
export function matchesRosterQuery(official: ReportProgressRow, needle: string): boolean {
  const tokens = needle.split(/\s+/).filter((token) => token !== "");
  if (tokens.length === 0) return true;
  const haystack = [official.mediaName, official.country, official.role, official.email ?? ""]
    .join(" ")
    .toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}

export interface RosterPanelProps {
  /** Used for the panel's heading id and its tab ids — one per panel, so they cannot collide. */
  id: string;
  title: string;
  note: string;
  tabsAriaLabel: string;
  /** The tabs, after the implicit `All`. */
  roles: readonly OfficialRole[];
  /** Everybody in this panel, before the tab filter. */
  officials: readonly ReportProgressRow[];
  /** Which tab is selected — `null` is `All`. */
  selectedRole: OfficialRole | null;
  onSelectRole: (role: OfficialRole | null) => void;
  /**
   * The roster search text, narrowing the rows on top of the role tab. Omitted — as the
   * Reporting page omits it — nothing is narrowed and this panel behaves exactly as before.
   */
  query?: string;
  /**
   * How large this panel draws its profile pictures and its Country cell — RUN 31.
   *
   * The Technical Delegate asked, while testing, for the pictures on **The Tournament Roster**
   * to be twice the size and the flag and its code to be about 80% bigger. This component
   * draws four panels across two pages: the roster's two and the Reporting page's §A4 two. He
   * was reading the roster, so the enlargement is asked for by that page and by that page
   * alone — `xl` — and Reporting keeps the `lg` sizes it has drawn since run 29.
   *
   * The value drives BOTH halves of the change: the avatar's own modifier, and a
   * `rostertable--xl` class on the table that the flag, the code, the column widths and the
   * row height hang off. One prop, so the two cannot be set to disagree.
   */
  avatarSize?: "lg" | "xl";
  /**
   * Draw the `FIH Grading` column after `Cap`. The Tournament Roster asks for it on both of
   * its panels; the Reporting page does not, and keeps the eight columns §A4 gives it. It adds
   * a `rostertable--graded` class the column widths and the phone card hang off, so a table
   * without the column is never laid out as though it had one.
   */
  showGrading?: boolean;
  /**
   * A head action, drawn after the panel note - the Technical Officials panel's `Push All Ready To
   * TMS` (2 Oct 2026). Omitted, the head is exactly as before.
   */
  headAction?: React.ReactNode;
  /** What that action has to say, drawn between the head and the table. Omitted, nothing is. */
  beneathHead?: React.ReactNode;
  /** The panel foot, verbatim per §A4 and §A3 of the profiles spec. */
  children: React.ReactNode;
}

export function RosterPanel({
  id,
  title,
  note,
  tabsAriaLabel,
  roles,
  officials,
  selectedRole,
  onSelectRole,
  query,
  avatarSize = "lg",
  showGrading = false,
  headAction,
  beneathHead,
  children,
}: RosterPanelProps): React.JSX.Element {
  const needle = (query ?? "").trim().toLowerCase();
  const shown = officials.filter(
    (official) =>
      (selectedRole === null || official.role === selectedRole) &&
      matchesRosterQuery(official, needle),
  );

  return (
    <section className="panel panel--d1" aria-labelledby={`${id}Title`} style={{ marginTop: 18 }}>
      <div className="panel__head">
        <h3 className="panel__title" id={`${id}Title`}>
          {title}
        </h3>
        <span className="panel__note">{note}</span>
        {headAction}
        <span className="spacer" />
        <span className="filtlab">Role</span>
        <div className="roletabs" role="group" aria-label={tabsAriaLabel}>
          <button
            type="button"
            className="roletab"
            aria-pressed={selectedRole === null}
            onClick={() => {
              onSelectRole(null);
            }}
          >
            All
          </button>
          {roles.map((role) => (
            <button
              key={role}
              type="button"
              className="roletab"
              aria-pressed={selectedRole === role}
              onClick={() => {
                onSelectRole(role);
              }}
            >
              {role}
            </button>
          ))}
        </div>
      </div>

      {beneathHead}

      {shown.length === 0 ? (
        <EmptyState
          title="No officials shown"
          detail={
            officials.length === 0
              ? "This panel holds nobody. Load the roster from TMS first."
              : needle === ""
                ? "No official on this panel holds that role. Choose All to see the whole panel."
                : "Nobody on this panel matches that search. Clear it, or choose All, to see the whole panel."
          }
        />
      ) : (
        <table
          className={[
            "mtable sumtable rostertable",
            avatarSize === "xl" ? "rostertable--xl" : null,
            showGrading ? "rostertable--graded" : null,
          ]
            .filter((name) => name !== null)
            .join(" ")}
          data-testid={`${id}Table`}
        >
          {/*
            The mockup's solved column widths. `reporting-page-v1.html` records how it got
            them — measured across all forty-eight rows and chosen to minimise the variance of
            the gaps between adjacent cell contents — and `Report Status` takes its 12% from
            `Mobile Number`, the least-scanned column on the row. Without `table-layout:
            fixed` and this colgroup, `Full Name` stretches and squeezes `Country` against it.
          */}
          <colgroup>
            <col className="w-role" />
            <col className="w-pic" />
            <col className="w-name" />
            <col className="w-ctry" />
            <col className="w-cap" />
            {showGrading ? <col className="w-grade" /> : null}
            <col className="w-rep" />
            <col className="w-mail" />
            <col className="w-tel" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Official Role</th>
              <th scope="col">
                <span className="sr-only">Profile Picture</span>
              </th>
              <th scope="col">Full Name</th>
              <th scope="col" className="c-ctry-h">
                Country
              </th>
              <th scope="col" className="c-cap-h" style={{ textAlign: "right" }}>
                Cap
              </th>
              {showGrading ? <th scope="col">FIH Grading</th> : null}
              <th scope="col">Report Status</th>
              <th scope="col">Email Address</th>
              <th scope="col">Mobile Number</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((official) => (
              <tr
                key={official.tmsPersonId}
                className={official.gender === null ? undefined : `g-${official.gender}`}
                data-testid={`row-${official.tmsPersonId}`}
              >
                <td className="cell-role" data-label="Official Role">
                  <span className={`rolechip2 rolechip2--${roleChipModifier(official.role)}`}>
                    {official.role}
                  </span>
                </td>
                {/*
                  Run 29 — the face, at `lg`. The TD asked for the photograph in this column
                  and for the circle to grow so that a face reads at a glance down forty-eight
                  rows; the initials circle is still what an official without a photograph gets,
                  at the same new size, so the column stays one column rather than two.

                  Run 31 — the roster page asks for `xl`, twice `lg`. The initials circle takes
                  the same size for the same reason it took the same size in run 29.
                */}
                <td className="cell-pic" data-label="Profile Picture">
                  <OfficialAvatar
                    tmsPersonId={official.tmsPersonId}
                    mediaName={official.mediaName}
                    gender={official.gender}
                    photoRef={official.photoRef}
                    size={avatarSize}
                  />
                </td>
                <td className="cell-name" data-label="Full Name">
                  <Link
                    className="rowlink"
                    to={`/officials/${official.tmsPersonId}`}
                    target="_blank"
                    rel="noopener"
                    title={`Open ${official.mediaName}'s Profile In A New Tab`}
                  >
                    {official.mediaName}
                  </Link>
                </td>
                <td className="cell-ctry" data-label="Country">
                  <CountryFlag code={official.country} />
                </td>
                <td className="num cell-cap" data-label="Cap" title={capTooltip(official.role)}>
                  {official.cap === null ? (
                    <span className="cell-none">—</span>
                  ) : (
                    <>
                      {official.cap}
                      {official.capUnit === null ? null : (
                        <span className="of"> {CAP_UNIT_SHORT[official.capUnit]}</span>
                      )}
                    </>
                  )}
                </td>
                {showGrading ? (
                  <td className="cell-grade" data-label="FIH Grading">
                    {official.gradingLevel ?? <span className="cell-none">—</span>}
                  </td>
                ) : null}
                <td className="cell-status" data-label="Report Status">
                  <ReportStatusPill stage={official.stage} variant="chip" />
                  {official.ownedByUm ? (
                    <OwnedByUmMarker ownerUmShortName={official.ownerUmShortName} />
                  ) : null}
                </td>
                <td className="cell-email" data-label="Email Address">
                  {official.email === null ? (
                    <span className="cell-none">Not Recorded</span>
                  ) : (
                    <a
                      className="rowlink"
                      href={`mailto:${official.email}`}
                      title={`Write To ${official.mediaName}`}
                    >
                      {official.email}
                    </a>
                  )}
                </td>
                <td className="cell-mobile num" data-label="Mobile Number">
                  {official.mobile ?? <span className="cell-none">Not Recorded</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="panel__foot">{children}</div>

      {/*
        The count line sits in its own `.pager` strip UNDER the panel foot, which is where
        both roster mockups put it: the foot is prose about the column meanings and the count
        is a reading of the table, so the hairline between them is the mockup saying they are
        two different kinds of statement. §A3 names this element `#techInfo` / `#umpInfo`; the
        id is carried so the spec's own handle resolves as well as the testid the tests read.
      */}
      <div className="pager" aria-label={`${title} count`}>
        <span className="pager__info num" id={`${id}Info`} data-testid={`${id}Count`}>
          {shown.length} Of {officials.length} Officials Shown
        </span>
      </div>
    </section>
  );
}
